import React, { useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import type { ActionMeta, OnChangeValue } from "react-select";

import { faBolt, faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faChevronDown, faHashtag, faLink, faUserPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faBolt as faBoltS, faCaretDown, faLock } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { noop } from "lodash";
import { commitMutation } from "relay-runtime";

import { useToggle } from "~/lib/hooks";
import { warning } from "~/lib/prompts";
import recaptcha from "~/lib/recaptcha";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { withSuspense } from "~/lib/suspense";
import { isNotNull } from "~/lib/utils";
import { useCopyMeetingLink } from "~/meetings/hooks/useCopyMeetingLink";
import { environment } from "~/models";
import { Avatar, Button, Tooltip } from "~/ui";
import { ButtonLink, Modal, SimpleTabs } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Menu from "~/ui/menu";
import { __globalModalContext, type ModalProps, useModals } from "~/ui/modals";
import Selector from "~/ui/selectors/Selector";
import toast from "~/ui/toast";

import { BillingIdentity } from "../billing";
import { AppContext } from "../common";
import SendNotesModal from "../notes/SendNotesModal";
import { NoteDate } from "../stream/NoteHeader/NoteHeader";
import StreamSettingsModal from "../stream/StreamSettings";
import { useAnySendAutomationsEnabled } from "../stream/StreamSettings/Automations/hooks";
import ContactChannelSelector, { type ContactChannelOption } from "./ContactChannelSelector";
import { PostedChannelList, SELECTOR_STYLES, VISIBILITY_OPTIONS, type VisibilityOption } from "./ShareClipModal";
import { useVisibilityChangeMutation as useRecordingVisibilityChangeMutation } from "./ShareModal";

import type {
	ShareMeetingModal_note$data as Note,
	ShareMeetingModal_note$key,
} from "~/__generated__/ShareMeetingModal_note.graphql";
import type { ShareMeetingModal_postedChannels$key } from "~/__generated__/ShareMeetingModal_postedChannels.graphql";
import type {
	ShareMeetingModal_sharedWith$key,
	ShareMeetingModal_sharedWith$data as ShareMeetingModalUsers,
} from "~/__generated__/ShareMeetingModal_sharedWith.graphql";
import type { ShareMeetingModalAddChannelMutation } from "~/__generated__/ShareMeetingModalAddChannelMutation.graphql";
import type { ShareMeetingModalAddPeopleMutation } from "~/__generated__/ShareMeetingModalAddPeopleMutation.graphql";
import type {
	ShareMeetingModalQuery,
	ShareMeetingModalQuery$data,
} from "~/__generated__/ShareMeetingModalQuery.graphql";
import type { ShareMeetingModalRemovePeopleMutation } from "~/__generated__/ShareMeetingModalRemovePeopleMutation.graphql";

import shareClipModalStyles from "./css/ShareClipModal.module.css";
import styles from "./css/ShareMeetingModal.module.css";

const TAB_VALUES = ["Share", "Send"] as const;
type TabType = (typeof TAB_VALUES)[number];

graphql`
	fragment ShareMeetingModal_SingleRecording on MeetingRecording {
		id
		title
		viewCount
		userVideoProgress
		isFavourite
		meetingRecordedVideoPosterUrl
		meetingRecordedVideoDuration
		meetingVideoAndTranscriptionDownloadUrl
		meetingRecordedVideoUrl
		meetingCallStart
		hasSummary
		attendees {
			email
			fullName
			image
			isTopUser
		}
		viewerCanDelete
		note {
			... on Note {
				# Only get the ID for Note and not RestrictedNote so the transcript
				# modal is opened via the MeetingRecording rather than the note which
				# the user doesn't have access to.
				id
				eventStart
				meetingRecordingLink {
					id
					visibility
				}
			}
		}
		ownerAccount {
			id
			name
		}
	}
`;

graphql`
	fragment ShareMeetingModal_ChannelPostContent on ChannelPostInterface {
		id

		clip {
			...ClipsPanel_RenameClipModal_clip
			...ClipsPanel_DeleteClipModal_clip
			id
			viewCount
			userVideoProgress
			isFavourite
			visibility
			viewerCanEdit
			meetingRecordedVideoPosterUrl
			meetingRecordedVideoDuration
			meetingRecordedVideoUrl
			title
			createdAt
			creator {
				id
				type
				email
				firstName
				fullName
				image
				isActive
				isConfigured
				isGuest
				isTopUser
				isExternal
			}

			ownerAccount {
				id
				name
			}
		}

		recording {
			...ShareMeetingModal_SingleRecording @relay(mask: false)
		}

		note {
			id
			title
			rawDraftJson
			eventStart
			noteuserSet {
				user {
					id
					email
					image
					fullName
					isTopUser
				}
			}
			longestMeetingRecording {
				...ShareMeetingModal_SingleRecording @relay(mask: false)
			}
		}
	}
`;

graphql`
	fragment ShareMeetingModal_Channel on Channel {
		id
		name
		postsCount
		isMember
		isPrivate
		isPersonal
		isWorkspaceWide
		isMyMeetings
		isFavorites
		posts(first: 4) {
			edges {
				node {
					...ShareMeetingModal_ChannelPostContent @relay(mask: false)
				}
			}
		}
	}
`;

const noteFragment = graphql`
	fragment ShareMeetingModal_note on Note {
		id
		title
		createdAt
		eventStart
		eventEnd
		hasMeetingRecordings
		viewerCanEdit
		stream {
			id
			type
			...hooksAutomations_useAnySendAutomationsEnabled_stream
		}
		meetingRecordingLink {
			id
			visibility
		}
		account {
			allowPublicRecap
		}
		...SendNotesModal_note
		...ShareMeetingModal_sharedWith
		...ShareMeetingModal_postedChannels
		...useCopyMeetingLink_note
	}
`;

const modalQuery = graphql`
	query ShareMeetingModalQuery($noteId: ID!) {
		viewer {
			...SendNotesModal_viewer
			...BillingIdentityFeaturesPaywall_viewer
		}

		node(id: $noteId) {
			__typename
			... on Note {
				...ShareMeetingModal_note
			}
		}
	}
`;

const shareWithPeopleMutation = graphql`
	mutation ShareMeetingModalAddPeopleMutation($noteId: ID!, $emails: [String!]!, $captchaToken: String!) {
		shareSingleNote(noteId: $noteId, emails: $emails, captchaToken: $captchaToken) {
			note {
				...ShareMeetingModal_sharedWith
				stream {
					# not quite related to invitations, i know, but the other fragment we could use without
					# refactoring is stream_mainstream, which is way bigger for what we need here
					...InviteExternalToSeriesBanner_stream
				}
			}
		}
	}
`;

const unShareWithPeopleMutation = graphql`
	mutation ShareMeetingModalRemovePeopleMutation($noteId: ID!, $userId: ID!) {
		unshareSingleNote(noteId: $noteId, userId: $userId) {
			note {
				viewerCanEdit
				...ShareMeetingModal_sharedWith
				stream {
					# see comment in ShareMeetingModalAddPeopleMutation
					...InviteExternalToSeriesBanner_stream
				}
			}
		}
	}
`;

const shareToChannelsMutation = graphql`
	mutation ShareMeetingModalAddChannelMutation($noteId: ID!, $channelIds: [ID!]!) {
		shareToChannels(noteId: $noteId, channelIds: $channelIds) {
			note {
				...ShareMeetingModal_postedChannels
			}

			channels {
				...ShareMeetingModal_Channel
			}
		}
	}
`;

const sharedWithFragment = graphql`
	fragment ShareMeetingModal_sharedWith on Note {
		noteuserSet {
			id
			accessType
			user {
				id
				email
				fullName
				image
				isExternal
				isGuest
				isConfigured
			}
		}
	}
`;

const postedChannelsFragment = graphql`
	fragment ShareMeetingModal_postedChannels on Note {
		postedInChannels {
			id
			name
			isPrivate

			members {
				id
			}
		}
	}
`;

function ClarificationText({ text, icon }: { text: string; icon: IconDefinition }) {
	return (
		<p className={styles.clarificationText}>
			<FontAwesomeIcon fixedWidth icon={icon} className={styles.icon} />
			{text}
		</p>
	);
}

type UserAccess = ShareMeetingModalUsers["noteuserSet"][number];

function UserRow({
	userAccess,
	noteId,
	readonly = false,
}: {
	userAccess: UserAccess;
	noteId: string;
	readonly?: boolean;
}) {
	const { user: viewerUser } = React.useContext(AppContext);

	const { user, accessType } = userAccess;
	const [loading, setLoading] = React.useState(false);

	const userIsViewer = user.id === viewerUser?.id;

	const onRemove = React.useCallback(async () => {
		if (loading) return;

		const confirmText = userIsViewer
			? "Are you sure you want to leave? You may not be able to access this meeting anymore."
			: `Are you sure you want to remove ${user.fullName}?` +
				` ${user.fullName} may not be able to access this meeting anymore.`;

		const result = await warning(confirmText, { yesText: userIsViewer ? "Leave" : "Remove", title: "Are you sure?" });

		if (typeof result !== "boolean" || !result) {
			return;
		}

		setLoading(true);
		commitMutation<ShareMeetingModalRemovePeopleMutation>(environment, {
			mutation: unShareWithPeopleMutation,
			variables: { noteId, userId: user.id },
			onCompleted: ({ unshareSingleNote }, errors) => {
				if (errors?.length || !unshareSingleNote?.note) {
					toast.error(errors?.[0]?.message || "Unable to remove this user access");
				} else {
					toast.success("Unshared with user");
				}

				setLoading(false);
			},
		});
	}, [user.id, user.fullName, loading, noteId, userIsViewer]);

	const userCanBeRemoved =
		!accessType.includes("INTRINSIC") &&
		!accessType.includes("EVENT") &&
		!accessType.includes("STREAM_SHARE") &&
		!readonly;

	const inviteIndicator = <span className={styles.inviteIndicator}>(Pending: Invite sent)</span>;

	return (
		<div className={styles.userRow}>
			<Avatar user={user} className={styles.avatar} />
			<p className={styles.name}>
				{user.fullName} {userIsViewer && "(you)"}
				{!user.isConfigured && inviteIndicator}
			</p>
			{userCanBeRemoved ? (
				<Menu>
					<Menu.Trigger>
						<Button size="slim" className={shareClipModalStyles.menuTrigger}>
							Can edit <FontAwesomeIcon icon={faCaretDown} className={shareClipModalStyles.icon} />
						</Button>
					</Menu.Trigger>
					<Menu.Content>
						<Menu.RadioGroup value="view">
							<Menu.RadioItem value="view" className={shareClipModalStyles.radioItem}>
								Can edit
							</Menu.RadioItem>
						</Menu.RadioGroup>
						<Menu.Separator />
						<Menu.Item destructive onSelect={onRemove}>
							{userIsViewer ? "Leave" : "Remove"}…
						</Menu.Item>
					</Menu.Content>
				</Menu>
			) : (
				<Tooltip
					content={
						accessType.includes("EVENT")
							? "This person is locked because they are an attendee of the event"
							: "This person is locked because the meeting series was shared with them"
					}
					placement="top"
					maxWidth={accessType.includes("EVENT") ? "11em" : "13.25em"}
					className={shareClipModalStyles.tooltip}
					disabled={readonly}
				>
					<p className={styles.lockedUser}>
						Can edit
						{!readonly && <FontAwesomeIcon icon={faLock} className={styles.icon} />}
					</p>
				</Tooltip>
			)}
		</div>
	);
}

function SharedWithList({
	usersAccess,
	noteId,
	initialMaxNumUsers = 5,
}: {
	usersAccess: readonly UserAccess[];
	noteId: string;
	initialMaxNumUsers?: number;
}) {
	const { user: viewerUser } = React.useContext(AppContext);

	const [showAll, toggleShowAll] = useToggle(false);
	const viewerUserAccess = usersAccess.find(userAccess => userAccess.user.id === viewerUser?.id);
	const readonly = viewerUserAccess === undefined || viewerUserAccess.user.isGuest;

	const sortedUsersAccess = React.useMemo(() => {
		if (viewerUserAccess) {
			return [
				viewerUserAccess,
				...usersAccess
					.filter(userAccess => userAccess.id !== viewerUserAccess.id)
					.sort((a, b) => a.user.fullName.localeCompare(b.user.fullName)),
			];
		}

		return [...usersAccess].sort((a, b) => a.user.fullName.localeCompare(b.user.fullName));
	}, [viewerUserAccess, usersAccess]);

	const totalUsers = sortedUsersAccess.length;
	const numHiddenUsers = showAll || totalUsers <= initialMaxNumUsers ? 0 : totalUsers - initialMaxNumUsers;
	const visibleUsersAccess = numHiddenUsers === 0 ? sortedUsersAccess : sortedUsersAccess.slice(0, initialMaxNumUsers);

	return (
		<div className={shareClipModalStyles.accessListContainer}>
			<p className={shareClipModalStyles.label}>
				People with access:
				<Tooltip
					content="People have direct access to edit the Note and the Recap for this meeting"
					placement="top"
					maxWidth="13.75em"
					className={shareClipModalStyles.tooltip}
				>
					<FontAwesomeIcon icon={faInfoCircle} className={shareClipModalStyles.infoIcon} />
				</Tooltip>
			</p>

			{visibleUsersAccess.map(userAccess => (
				<UserRow key={userAccess.id} userAccess={userAccess} noteId={noteId} readonly={readonly} />
			))}

			{totalUsers === 0 && (
				<p className={shareClipModalStyles.emptyMessage}>This meeting has not yet been shared with any channels.</p>
			)}

			{numHiddenUsers > 0 && (
				<ButtonLink className={shareClipModalStyles.viewMoreButton} onClick={toggleShowAll}>
					<FontAwesomeIcon icon={faChevronDown} className={shareClipModalStyles.icon} />
					View more ({numHiddenUsers})
				</ButtonLink>
			)}
		</div>
	);
}

function ModalFooter({ note, readonly = false }: { note: Note; readonly?: boolean }) {
	const copyLink = useCopyMeetingLink(note);

	const [changeRecordingVisibility, mutationInProgress] = useRecordingVisibilityChangeMutation();
	const onChangeVisibility = React.useCallback(
		async (option: VisibilityOption | null) => {
			if (!option || mutationInProgress) return;

			try {
				await changeRecordingVisibility(note.id, option.value, note.meetingRecordingLink.id);
				toast.success("Visibility updated successfully");
			} catch (e) {
				toast.error("Something went wrong, please try again later", { title: "Failed to change visibility" });
			}
		},
		[note.id, note.meetingRecordingLink.id, changeRecordingVisibility, mutationInProgress],
	);

	const visibilityOption = VISIBILITY_OPTIONS.find(opt => opt.value === note.meetingRecordingLink.visibility);
	const visibilityIsPrivate = note.meetingRecordingLink.visibility === "PRIVATE";
	const hasRecordings = note.hasMeetingRecordings;

	return (
		<Modal.Footer className={shareClipModalStyles.modalFooter}>
			<div className={shareClipModalStyles.visibilityContainer}>
				<Tooltip
					content="Meeting attendees, shared people, and shared channels "
					placement="top"
					maxWidth="10.5em"
					className={shareClipModalStyles.tooltip}
					disabled={!visibilityIsPrivate}
				>
					{hasRecordings ? (
						readonly ? (
							<p className={cx(shareClipModalStyles.text, shareClipModalStyles.slim)}>
								{visibilityOption?.label}
								{visibilityIsPrivate ? " to Note and Recap" : " can view the Recap"}
							</p>
						) : (
							<div>
								<Selector<VisibilityOption>
									className={shareClipModalStyles.selector}
									condensed
									options={VISIBILITY_OPTIONS.map(option => ({
										...option,
										isDisabled: option.value === "PUBLIC" && !note.account.allowPublicRecap,
									}))}
									value={visibilityOption}
									onChange={onChangeVisibility}
									styles={SELECTOR_STYLES}
									isDisabled={mutationInProgress}
									menuPlacement="auto"
								/>
							</div>
						)
					) : (
						<p className={cx(shareClipModalStyles.text, shareClipModalStyles.slim)}>Restricted access to Note</p>
					)}
				</Tooltip>

				{hasRecordings && !readonly && (
					<p className={shareClipModalStyles.text}>
						{visibilityIsPrivate ? "to Note and Recap" : "can view the Recap"}
					</p>
				)}

				<ButtonLink className={shareClipModalStyles.copyLink} onClick={copyLink}>
					<FontAwesomeIcon icon={faLink} className={shareClipModalStyles.icon} /> Copy link
				</ButtonLink>
			</div>
		</Modal.Footer>
	);
}

type ShareMeetingModalProps = ModalProps<unknown> & {
	note: ShareMeetingModal_note$key;
	viewer: ShareMeetingModalQuery$data["viewer"];
	initialTab?: TabType;
};

function ShareMeetingModal({ close, note: noteKey, viewer, initialTab = TAB_VALUES[0] }: ShareMeetingModalProps) {
	const note = useFragment<ShareMeetingModal_note$key>(noteFragment, noteKey);
	const { noteuserSet } = useFragment<ShareMeetingModal_sharedWith$key>(sharedWithFragment, note);
	const { postedInChannels } = useFragment<ShareMeetingModal_postedChannels$key>(postedChannelsFragment, note);

	const viewerHasChannelAccessOnly = !note.viewerCanEdit;

	const [activeTab, setActiveTab] = React.useState<TabType>(initialTab);
	const [loading, setLoading] = React.useState(false);

	const { user: viewerUser } = React.useContext(AppContext);

	// Share tab
	const [contactsAndChannels, setContactsAndChannels] = React.useState<readonly ContactChannelOption[]>([]);
	const onClearContactsAndChannels = React.useCallback(() => setContactsAndChannels([]), []);
	const onChangeContactsAndChannels = React.useCallback(
		(value: OnChangeValue<ContactChannelOption, boolean>, _meta: ActionMeta<ContactChannelOption>) => {
			if (value) {
				if (value instanceof Array) {
					setContactsAndChannels(value);
				} else {
					setContactsAndChannels([value]);
				}
			} else {
				setContactsAndChannels([]);
			}
		},
		[],
	);

	const onShare = React.useCallback(async () => {
		if (loading) return;

		setLoading(true);

		const emails = contactsAndChannels
			.map(contactOrChannel => (contactOrChannel.contact ? contactOrChannel.contact.email : null))
			.filter(isNotNull);

		const channelIds = contactsAndChannels
			.map(contactOrChannel => (contactOrChannel.contact ? null : contactOrChannel.value))
			.filter(isNotNull);

		if (emails.length > 0) {
			const captchaToken = await recaptcha("user_dialogues");

			commitMutation<ShareMeetingModalAddPeopleMutation>(environment, {
				mutation: shareWithPeopleMutation,
				variables: { noteId: note.id, emails, captchaToken },
				onCompleted: ({ shareSingleNote }, errors) => {
					if (errors?.length || !shareSingleNote?.note) {
						toast.error(errors?.[0]?.message || "Unable to share this meeting with people");
					} else {
						toast.success(`You've shared this note with ${emails.length} teammate${emails.length > 1 ? "s" : ""}`);
					}
				},
			});
		}

		if (channelIds.length > 0) {
			commitMutation<ShareMeetingModalAddChannelMutation>(environment, {
				mutation: shareToChannelsMutation,
				variables: { noteId: note.id, channelIds },
				onCompleted: ({ shareToChannels }, errors) => {
					if (errors?.length || !shareToChannels?.note) {
						toast.error(
							errors?.[0]?.message || `Unable to share this meeting in channel${channelIds.length > 1 ? "s" : ""}`,
						);
					} else {
						toast.success(`Shared with ${channelIds.length} channel${channelIds.length > 1 ? "s" : ""}`);
					}
				},
			});
		}

		onClearContactsAndChannels();
		setLoading(false);
	}, [loading, note.id, contactsAndChannels, onClearContactsAndChannels]);

	const filterOption = React.useCallback(
		(option: ContactChannelOption) => {
			const optionIsListed =
				postedInChannels.some(c => c.id === option.value) ||
				noteuserSet.some(nu => nu.user.email.toLowerCase() === option.value.toLowerCase());

			return !optionIsListed;
		},
		[postedInChannels, noteuserSet],
	);

	const invitationWillBeSent = React.useMemo(
		() =>
			contactsAndChannels.some(contactOrChannel =>
				contactOrChannel.contact ? !contactOrChannel.contact.hasConfiguredUser : false,
			),
		[contactsAndChannels],
	);

	const willBeSharedWithChannel = React.useMemo(
		() => contactsAndChannels.some(contactOrChannel => !contactOrChannel.contact),
		[contactsAndChannels],
	);

	const noteDateEvent =
		note.eventStart && note.eventEnd
			? { start: new Date(note.eventStart), end: new Date(note.eventEnd), isAllDay: false }
			: null;

	const noteDateCreatedAt = note.createdAt ? new Date(note.createdAt) : null;
	const noteInfo = (
		<div className={styles.noteInfoContainer}>
			<p className={styles.title} title={note.title}>
				{note.title}
			</p>
			<span className={styles.separator} />
			<NoteDate createdAt={noteDateCreatedAt} isCancelled={false} event={noteDateEvent} />
		</div>
	);

	const infoButtons = (
		<div className={styles.infoButtons}>
			<Tooltip
				content="You are sharing a single meeting. Sharing gives access to both the Note and the Recap."
				placement="bottom"
				maxWidth="13.75em"
				className={shareClipModalStyles.tooltip}
			>
				<p className={styles.infoText}>What am I sharing?</p>
			</Tooltip>
			{contactsAndChannels.length > 0 && (
				<ButtonLink onClick={onClearContactsAndChannels} className={styles.clearButton}>
					Clear
				</ButtonLink>
			)}
		</div>
	);

	const selectNewChannel = useCallback((channel: { id: string; name: string; isPrivate: boolean }) => {
		setContactsAndChannels(prevSelections => [
			...prevSelections,
			{
				label: channel.name,
				value: channel.id,
				icon: channel.isPrivate ? faLock : faHashtag,
			},
		]);
	}, []);

	const streamIsOneOnOne = note.stream.type === "oneonone";
	const shareTabContent = (
		<>
			<div className={styles.infoContainer}>
				{noteInfo}
				{infoButtons}
			</div>

			<div className={shareClipModalStyles.inputContainer}>
				<ContactChannelSelector
					placeholder={streamIsOneOnOne ? "Add channels…" : "Add names, emails, or channels…"}
					value={contactsAndChannels}
					onChange={onChangeContactsAndChannels}
					filterOption={filterOption}
					afterNewChannelCreated={selectNewChannel}
					defaultOptions
					isMulti
					skipContacts={streamIsOneOnOne}
					autoFocus
					userIdsToExclude={noteuserSet.map(nu => nu.user.id)}
				/>

				<BillingIdentity feature="sharing_channels" viewer={viewer} iconInheritColor>
					{(extraProps, icon, paywalled) => (
						<Button
							variant={paywalled ? "billing" : "primary"}
							disabled={contactsAndChannels.length === 0 || loading}
							onClick={onShare}
							className={shareClipModalStyles.button}
							{...extraProps}
						>
							Share {icon}
						</Button>
					)}
				</BillingIdentity>
			</div>

			{invitationWillBeSent && (
				<ClarificationText text="New teammates will be invited to join Fellow." icon={faUserPlus} />
			)}

			{willBeSharedWithChannel && (
				<ClarificationText text="Channel members will be able to view the Note and Recap." icon={faHashtag} />
			)}

			<SharedWithList usersAccess={noteuserSet} noteId={note.id} />

			<PostedChannelList channels={postedInChannels} noteId={note.id} />
		</>
	);

	// Send tab
	const modals = useModals();
	const streamId = note.stream.id;
	const anyAutomationsEnabled = useAnySendAutomationsEnabled(note.stream);

	const onClickStreamSettings = React.useCallback(
		() =>
			modals.open(({ close }) => {
				return <StreamSettingsModal streamId={streamId} close={close} defaultSection="automations" />;
			}),
		[modals, streamId],
	);

	const infoAutomations = (
		<div className={styles.infoButtons}>
			<ButtonLink onClick={onClickStreamSettings} className={styles.automationButton}>
				<FontAwesomeIcon fixedWidth icon={anyAutomationsEnabled ? faBoltS : faBolt} className={styles.icon} />
				{anyAutomationsEnabled ? "View Automations" : "Automate sending…"}
			</ButtonLink>
		</div>
	);

	const sendTabContent = (
		<>
			<div className={styles.infoContainer}>
				{noteInfo}
				{!viewerUser?.isGuest && infoAutomations}
			</div>

			<SendNotesModal note={note} onClose={close} viewer={viewer} />
		</>
	);

	// Access tab
	const accessTabContent = (
		<>
			<div className={styles.infoContainer}>{noteInfo}</div>

			<SharedWithList usersAccess={noteuserSet} noteId={note.id} />
			<PostedChannelList channels={postedInChannels} noteId={note.id} readonly />
		</>
	);

	return (
		<Modal onClose={close} width="500px">
			<Modal.Header className={shareClipModalStyles.modalHeader}>
				{viewerHasChannelAccessOnly ? (
					<SimpleTabs tabs={["Access"]} tab="Access" setTab={noop} className={shareClipModalStyles.tabs} />
				) : (
					<SimpleTabs tabs={TAB_VALUES} tab={activeTab} setTab={setActiveTab} className={shareClipModalStyles.tabs} />
				)}
				<Modal.HeaderActions onClose={close} className={shareClipModalStyles.modalHeaderActions} />
			</Modal.Header>

			<Modal.Body className={shareClipModalStyles.modalBody}>
				{viewerHasChannelAccessOnly
					? accessTabContent
					: activeTab === TAB_VALUES[0]
						? viewerUser?.isGuest
							? accessTabContent
							: shareTabContent
						: sendTabContent}
			</Modal.Body>

			<ModalFooter note={note} readonly={viewerHasChannelAccessOnly || viewerUser?.isGuest} />
		</Modal>
	);
}

type ShareMeetingModalRendererProps = ModalProps<unknown> & {
	noteId: string;
	initialTab?: TabType;
};

function ShareMeetingModalRenderer({ close, noteId, initialTab }: ShareMeetingModalRendererProps) {
	const { node: note, viewer } = useLazyLoadQuery<ShareMeetingModalQuery>(
		modalQuery,
		{ noteId },
		{ fetchPolicy: "network-only" },
	);

	if (note?.__typename !== "Note") {
		return null;
	}

	return <ShareMeetingModal close={close} note={note} viewer={viewer} initialTab={initialTab} />;
}

const ShareMeetingModalRendererWithSuspense = withSuspense(<LoadingSpinner />, ShareMeetingModalRenderer);

export function openShareMeetingModal(noteId: string, initialTab?: TabType) {
	__globalModalContext?.open(({ close }) => (
		<ShareMeetingModalRendererWithSuspense close={close} noteId={noteId} initialTab={initialTab} />
	));
}
